// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useState } from 'react';

import {
  type PartCatalog,
  type PartListPart,
  type PartCategory,
  type PartCategoryId,
  type PartId,
  PART_TYPE_ASSEMBLY_ID,
  useEvolvePaginatedGet,
} from '@evolvemep/api-ts';
import { Box, Card, Flex, type SelectProps, Text } from '@mantine/core';
import { useDebouncedState } from '@mantine/hooks';

import { EvolveIcon, type EvolveIconName } from 'assets/icons/EvolveIcon';
import type { WrappedSelectProps } from 'components/Mantine/TypeSafeSelect/types';
import { WrappedSelect } from 'components/Mantine/TypeSafeSelect/WrappedSelect';
import { isNil, isNotNil } from 'helpers/isNotNil';

const MIN_SEARCH_LENGTH = 2 as const;

type Props = {
  partCatalog: PartCatalog | null;
  onPartSelected: (part: PartListPart) => void;
  onCategorySelected: (category: PartCategory) => void;
  w?: SelectProps['w'];
  disabled?: boolean;
};

const maxResults = 10;

export const CatalogSearch = ({ partCatalog, onPartSelected, onCategorySelected, disabled, w }: Props) => {
  const [searchPhrase, setSearchPhrase] = useDebouncedState('', 350);
  const [partsSearchResult, setPartsSearchResult] = useState<PartListPart[]>([]);
  const [partsEntireCount, setPartsEntireCount] = useState(0);
  const [categorySearchResult, setCategorySearchResult] = useState<PartCategory[]>([]);
  const [categoryEntireCount, setCategoryEntireCount] = useState(0);

  const { fetchPage: searchParts, loading: loadingSearchParts } = useEvolvePaginatedGet('shop/partCatalog/part/search');

  const { fetchPage: searchPartCategories, loading: loadingSearchPartCategories } = useEvolvePaginatedGet(
    'shop/partCatalog/category/search',
  );

  useEffect(() => {
    if (searchPhrase.length >= MIN_SEARCH_LENGTH) {
      const opts = {
        params: {
          partCatalogId: partCatalog?.partCatalogId,
          searchTerm: encodeURIComponent(searchPhrase),
        },
      };
      void searchParts({ skip: 0, take: maxResults }, opts).then((d) => {
        setPartsSearchResult(d.data);
        setPartsEntireCount(d.entireCount);
      });
      void searchPartCategories({ skip: 0, take: maxResults }, opts).then((d) => {
        setCategorySearchResult(d.data);
        setCategoryEntireCount(d.entireCount);
      });
    } else {
      setPartsSearchResult([]);
      setCategorySearchResult([]);
    }
  }, [searchPartCategories, searchParts, searchPhrase, partCatalog?.partCatalogId]);

  return (
    <WrappedSelect<PartCategoryId | PartId>
      placeholder="Search catalog..."
      disabled={isNil(partCatalog) || disabled}
      searchable
      rightSection={<EvolveIcon icon={null} />}
      value={null}
      onChange={(id) => {
        setSearchPhrase('');
        const selectedPartCategory = categorySearchResult.find((c) => c.partCategoryId === id);
        if (isNotNil(selectedPartCategory)) {
          onCategorySelected(selectedPartCategory);
          return;
        }
        const selectedPart = partsSearchResult.find((p) => p.partId === id);
        if (isNotNil(selectedPart)) {
          onPartSelected(selectedPart);
        }
      }}
      nothingFoundMessage={
        searchPhrase.length < MIN_SEARCH_LENGTH || loadingSearchPartCategories || loadingSearchParts
          ? 'Start typing...'
          : undefined
      }
      w={w}
      maxDropdownHeight="min(90vh, 620px)"
      autoDropdownWidth
      searchValue={searchPhrase}
      onSearchChange={setSearchPhrase}
      /* oxlint-disable-next-line no-use-before-define */
      renderOption={ItemRenderer}
      noFilterOnSearch
      data={
        searchPhrase.length >= MIN_SEARCH_LENGTH
          ? [
              {
                group: loadingSearchPartCategories
                  ? 'Categories'
                  : `Categories (showing ${categorySearchResult.length} of ${categoryEntireCount})`,
                items:
                  categorySearchResult.length === 0 || loadingSearchPartCategories
                    ? [
                        {
                          label: loadingSearchPartCategories ? 'Loading...' : 'No categories found.',
                          // Hacky solution to create a visible loading state
                          value: 'loadingCategories' as PartCategoryId,
                          disabled: true,
                        } as const,
                      ]
                    : categorySearchResult.map((c) => ({
                        label: c.name,
                        value: c.partCategoryId,
                        category: c,
                      })),
              },
              {
                group: loadingSearchParts
                  ? 'Parts'
                  : `Parts (showing ${partsSearchResult.length} of ${partsEntireCount})`,
                items:
                  partsSearchResult.length === 0 || loadingSearchParts
                    ? [
                        {
                          label: loadingSearchParts ? 'Loading...' : 'No parts found.',
                          // Hacky solution to create a visible loading state
                          value: 'loadingParts' as PartId,
                          disabled: true,
                        } as const,
                      ]
                    : partsSearchResult.map((p) => ({
                        label: p.name,
                        value: p.partId,
                        part: p,
                      })),
              },
            ]
          : []
      }
    />
  );
};

type ItemProps = {
  part?: PartListPart;
  category?: PartCategory;
  label?: string;
};

const ResultRenderer = ({
  label,
  description,
  icon,
}: {
  label: string;
  description?: string | null;
  icon: EvolveIconName;
}) => (
  <Flex gap="xs" style={{ overflow: 'hidden' }} title={label}>
    <Card p={0} withBorder w={40} h={40} miw={40}>
      <Flex style={{ height: '100%', width: '100%' }} align="center" justify="center">
        <EvolveIcon icon={icon} light />
      </Flex>
    </Card>
    <Flex direction="column" justify="center">
      <Text lineClamp={1}>{label}</Text>
      <Text lineClamp={2} fz="xs" opacity={0.6} style={{ whiteSpace: 'pre-wrap' }}>
        {description?.replaceAll(
          '/',
          // Invisible character to assist in line-breaks
          '\u200B/',
        )}
      </Text>
    </Flex>
  </Flex>
);

const ItemRenderer: WrappedSelectProps<PartCategoryId | PartId, ItemProps>['renderOption'] = (opts) => {
  const { part, category, label } = opts.option;
  return (
    <Box fz="sm">
      {isNotNil(part) && (
        <ResultRenderer
          label={part.name}
          description={`Mfg: ${part.manufacturerName}`}
          icon={part.partTypeId === PART_TYPE_ASSEMBLY_ID ? 'Assembly' : 'Part'}
        />
      )}
      {isNotNil(category) && (
        <ResultRenderer label={category.name} description={category.path.join('/')} icon="Folder" />
      )}
      {isNil(part) && isNil(category) && <span>{label}</span>}
    </Box>
  );
};
